import { Box } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';

export interface HealthRingsDataPoint {
  label: string;
  value: number;
  color: string;
  isNa?: boolean;
}

export interface HealthRingsProps {
  data: HealthRingsDataPoint[];
  size?: number;
}

const ANIM_DURATION = 900;
const MARGIN = 14;
// TW:TG ratio kept constant; values scale to always fit all rings
const TW_TG_RATIO = 2.5;

const HealthRings = ({ data, size = 380 }: HealthRingsProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | undefined>(undefined);
  const startRef = useRef<number | undefined>(undefined);

  const [animProgress, setAnimProgress] = useState(0);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number } | null>(null);

  // Sort: lowest score outermost (most attention), N/A items innermost
  const sortedData = useMemo(() => {
    const valid = [...data].filter((d) => !d.isNa).sort((a, b) => a.value - b.value);
    const na = data.filter((d) => d.isNa);
    return [...valid, ...na];
  }, [data]);

  const dataKey = sortedData.map((d) => d.value).join(',');

  useEffect(() => {
    setAnimProgress(0);
    startRef.current = undefined;
    if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current);

    const tick = (ts: number) => {
      if (startRef.current === undefined) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const t = Math.min(elapsed / ANIM_DURATION, 1);
      setAnimProgress(1 - Math.pow(1 - t, 3));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current);
    };
  }, [dataKey]);

  const center = size / 2;
  const n = sortedData.length || 1;
  const available = center - MARGIN;
  // Solve: n*TW + (n-1)*TG = available, where TW = TW_TG_RATIO * TG
  const trackGap = Math.max(4, Math.floor(available / (n * TW_TG_RATIO + n - 1)));
  const trackWidth = Math.max(8, Math.floor(TW_TG_RATIO * trackGap));
  const outerRadius = center - trackWidth / 2 - MARGIN;

  const hoveredPoint = hoveredIdx !== null ? sortedData[hoveredIdx] : null;

  return (
    <Box
      ref={containerRef}
      sx={{ position: 'relative', width: '100%', maxWidth: size, mx: 'auto' }}>
      <svg
        viewBox={`0 0 ${size} ${size}`}
        width="100%"
        style={{ display: 'block', overflow: 'visible' }}
        onMouseLeave={() => {
          setHoveredIdx(null);
          setTooltip(null);
        }}
        onMouseMove={(e) => {
          if (!containerRef.current) return;
          const rect = containerRef.current.getBoundingClientRect();
          setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top });
        }}>
        {sortedData.map((point, i) => {
          const r = outerRadius - i * (trackWidth + trackGap);
          if (r <= trackWidth / 2) return null;
          const circumference = 2 * Math.PI * r;
          const fillRatio = point.isNa ? 0 : Math.min(point.value / 100, 1) * animProgress;
          const filled = fillRatio * circumference;
          const isHovered = hoveredIdx === i;
          // Rotate -90° around center so the arc always starts exactly at 12 o'clock,
          // regardless of radius. Using dashOffset instead causes per-ring drift because
          // the rounded linecap is a fixed pixel size that corresponds to different angles
          // on rings with different radii.
          const rotateOrigin = `rotate(-90 ${center} ${center})`;

          return (
            <g
              key={i}
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setHoveredIdx(i)}>
              {/* Background track */}
              <circle
                cx={center}
                cy={center}
                r={r}
                fill="none"
                stroke={point.color}
                strokeWidth={trackWidth}
                opacity={isHovered ? 0.22 : 0.1}
                style={{ transition: 'opacity 0.2s' }}
              />
              {/* Progress arc */}
              {!point.isNa && (
                <circle
                  cx={center}
                  cy={center}
                  r={r}
                  fill="none"
                  stroke={point.color}
                  strokeWidth={trackWidth}
                  strokeDasharray={`${filled} ${circumference}`}
                  strokeLinecap="round"
                  transform={rotateOrigin}
                  style={{
                    filter: isHovered
                      ? `drop-shadow(0 0 6px ${point.color}90)`
                      : `drop-shadow(0 0 3px ${point.color}60)`,
                    transition: 'filter 0.2s',
                  }}
                />
              )}
              {/* Invisible wider hit area so thin rings are easy to hover */}
              <circle
                cx={center}
                cy={center}
                r={r}
                fill="none"
                stroke="transparent"
                strokeWidth={trackWidth + 10}
              />
            </g>
          );
        })}
      </svg>

      {/* Tooltip */}
      {hoveredPoint && tooltip && (
        <div
          style={{
            position: 'absolute',
            left: tooltip.x,
            top: tooltip.y - 12,
            transform: 'translate(-50%, -100%)',
            pointerEvents: 'none',
            background: 'var(--tx-glass)',
            backdropFilter: 'var(--tx-blur)',
            WebkitBackdropFilter: 'var(--tx-blur)',
            borderRadius: '12px',
            padding: '8px 14px',
            whiteSpace: 'nowrap',
            zIndex: 10,
            boxShadow: 'var(--tx-shadow-sm)',
          }}>
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: 'var(--tx-faint)',
              marginBottom: 2,
            }}>
            {hoveredPoint.label.replace(/_/g, ' ')}
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--tx-text)', lineHeight: 1 }}>
            {hoveredPoint.isNa ? (
              <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>N/A</span>
            ) : (
              <>
                {Math.round(hoveredPoint.value)}
                <span style={{ fontSize: 11, fontWeight: 400, color: 'rgba(255,255,255,0.35)', marginLeft: 2 }}>
                  /100
                </span>
              </>
            )}
          </div>
        </div>
      )}
    </Box>
  );
};

export default HealthRings;
